import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Clock, CheckCircle, XCircle, ArrowUpRight, Buildings, EnvelopeSimple,
  User, Globe, IdentificationCard, ChatCircleText, UsersThree, ShieldCheck, UserPlus,
} from "@phosphor-icons/react";
import { formatDate, timeAgo } from "@/lib/format";
import { Link } from "react-router-dom";

const CLASSIFICATIONS = [
  "Per Se Professional Investor",
  "Elective Professional Investor",
  "Pending Classification",
  "Rejected",
];

const InfoRow = ({ icon: Icon, label, children }) => (
  <div className="grid grid-cols-[24px_140px_1fr] gap-3 items-start py-2.5 border-b border-border/60 last:border-b-0">
    <Icon size={16} className="text-muted-foreground mt-0.5" />
    <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-muted-foreground pt-0.5">{label}</div>
    <div className="text-sm leading-relaxed">{children}</div>
  </div>
);

export default function AdminPendingApprovals() {
  const [tab, setTab] = useState("investors"); // investors | firms
  const [firms, setFirms] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFirm, setSelectedFirm] = useState(null);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pickClassification, setPickClassification] = useState("Per Se Professional Investor");

  const load = async () => {
    setLoading(true);
    try {
      const [f, i] = await Promise.all([
        api.get("/admin/investor-firms", { params: { status: "pending" } }),
        api.get("/admin/investor-applications"),
      ]);
      setFirms(f.data);
      setInvestors(i.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Firm actions
  const approveFirm = async (firm) => {
    setBusy(true);
    try {
      await api.post(`/admin/investor-firms/${firm.id}/approve`);
      toast.success(`${firm.name} approved. Notification email queued (mocked).`);
      setSelectedFirm(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(false); }
  };
  const rejectFirm = async (firm) => {
    if (!window.confirm(`Reject the registration request from ${firm.name}?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/investor-firms/${firm.id}/reject`);
      toast.success(`${firm.name} rejected.`);
      setSelectedFirm(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    finally { setBusy(false); }
  };

  // Investor actions
  const approveInvestor = async (u) => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${u.id}/classification?classification=${encodeURIComponent(pickClassification)}`);
      await api.patch(`/admin/users/${u.id}/approval?status=Approved`);
      toast.success(`${u.name} approved as ${pickClassification}`);
      setSelectedInvestor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(false); }
  };
  const rejectInvestor = async (u) => {
    if (!window.confirm(`Reject ${u.name}'s application?`)) return;
    setBusy(true);
    try {
      await api.patch(`/admin/users/${u.id}/classification?classification=Rejected`);
      await api.patch(`/admin/users/${u.id}/approval?status=Rejected`);
      toast.success(`${u.name} rejected`);
      setSelectedInvestor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Admin</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">Pending Approvals</h1>
        <p className="mt-2 text-muted-foreground text-sm max-w-2xl">
          Two queues need your review: individual investors from approved firms awaiting classification,
          and firms that aren&apos;t yet on the approved directory.
        </p>
      </div>

      {/* Tabs */}
      <div className="inline-flex gap-1 p-1 rounded-lg bg-secondary/60 mb-6">
        <button
          onClick={() => setTab("investors")}
          data-testid="tab-investor-applications"
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${tab === "investors" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
        >
          <UsersThree size={15} /> Investor Applications
          <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-bold ${tab === "investors" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-slate-200 dark:bg-slate-800"}`}>{investors.length}</span>
        </button>
        <button
          onClick={() => setTab("firms")}
          data-testid="tab-firm-applications"
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${tab === "firms" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
        >
          <Buildings size={15} /> Firm Applications
          <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-bold ${tab === "firms" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-slate-200 dark:bg-slate-800"}`}>{firms.length}</span>
        </button>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading…</div>
      ) : tab === "investors" ? (
        investors.length === 0 ? (
          <EmptyState
            heading="No investor applications waiting"
            body="Once an investor from an approved firm signs up + verifies their email, they'll appear here for classification review."
            ctas={[
              { to: "/admin/users", label: "Manage users", icon: UsersThree, testid: "empty-investors-cta-users" },
              { to: "/admin/investor-firms", label: "Approved firms", icon: Buildings, testid: "empty-investors-cta-firms", variant: "outline" },
            ]}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="investor-applications-grid">
            {investors.map((u) => (
              <button
                key={u.id}
                onClick={() => setSelectedInvestor(u)}
                data-testid={`pending-investor-${u.email.split("@")[0]}`}
                className="text-left rounded-2xl border border-border bg-card p-5 hover:border-emerald-400 dark:hover:border-emerald-700 hover:shadow-md transition-all group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 grid place-items-center text-emerald-700 dark:text-emerald-300">
                    <User size={20} />
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300">
                      <Clock size={11} /> {u.approval_status}
                    </span>
                    {u.fca_number && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-800/60"
                        title={`FCA Reference: ${u.fca_number}`}
                        data-testid={`fca-ref-pill-${u.email.split("@")[0]}`}
                      >
                        <ShieldCheck size={11} weight="fill" /> FCA ref
                      </span>
                    )}
                  </div>
                </div>
                <div className="font-display font-bold text-lg mb-0.5 group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors">{u.name}</div>
                <div className="text-xs text-muted-foreground mb-2">{u.email}</div>
                <div className="text-sm mb-3">{u.company || <span className="italic text-muted-foreground">Firm unknown</span>}</div>
                <div className="text-xs text-muted-foreground pt-3 border-t border-border flex items-center gap-2">
                  Registered {timeAgo(u.created_at)}
                </div>
              </button>
            ))}
          </div>
        )
      ) : (
        firms.length === 0 ? (
          <EmptyState
            heading="No firm applications waiting"
            body="Firms submitted by prospective investors that aren't yet on the approved directory will land here."
            ctas={[
              { to: "/admin/investor-firms", label: "Manage approved firms", icon: Buildings, testid: "empty-firms-cta-manage" },
              { to: "/admin/users", label: "Invite an investor", icon: UserPlus, testid: "empty-firms-cta-invite", variant: "outline" },
            ]}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="firm-applications-grid">
            {firms.map((f) => (
              <button
                key={f.id}
                onClick={() => setSelectedFirm(f)}
                data-testid={`pending-firm-${f.id}`}
                className="text-left rounded-2xl border border-border bg-card p-5 hover:border-amber-400 dark:hover:border-amber-700 hover:shadow-md transition-all group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="h-10 w-10 rounded-lg bg-amber-100 dark:bg-amber-900/40 grid place-items-center text-amber-800 dark:text-amber-300">
                    <Buildings size={20} />
                  </div>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300">
                    <Clock size={11} /> Pending
                  </span>
                </div>
                <div className="font-display font-bold text-lg mb-1 group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors">{f.name}</div>
                <div className="text-xs text-muted-foreground mb-3 flex flex-wrap gap-1.5">
                  {(f.domains || []).map((d) => <span key={d} className="font-mono">@{d}</span>)}
                </div>
                <div className="text-sm text-muted-foreground line-clamp-2 mb-3">{f.notes || <span className="italic">No notes provided</span>}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-2 pt-3 border-t border-border">
                  <User size={12} />
                  {f.requested_by_name || "—"} <span className="opacity-60">·</span>
                  <span className="num-display">{formatDate(f.created_at)}</span>
                </div>
              </button>
            ))}
          </div>
        )
      )}

      {/* Investor detail dialog */}
      <Dialog open={!!selectedInvestor} onOpenChange={(o) => !o && setSelectedInvestor(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{selectedInvestor?.name}</DialogTitle>
          </DialogHeader>
          {selectedInvestor && (
            <div className="mt-2">
              <InfoRow icon={EnvelopeSimple} label="Work email">
                <a href={`mailto:${selectedInvestor.email}`} className="text-emerald-700 dark:text-emerald-400 hover:underline">{selectedInvestor.email}</a>
                <span className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 font-bold uppercase tracking-wider">
                  <ShieldCheck size={10} weight="fill" /> Verified
                </span>
              </InfoRow>
              <InfoRow icon={Buildings} label="Firm">{selectedInvestor.company || "—"}</InfoRow>
              <InfoRow icon={ShieldCheck} label="FCA Reference">
                {selectedInvestor.fca_number ? (
                  <span className="font-mono text-sm">{selectedInvestor.fca_number}</span>
                ) : (
                  <span className="italic text-muted-foreground">Not provided</span>
                )}
              </InfoRow>
              <InfoRow icon={Clock} label="Approval status">{selectedInvestor.approval_status || "—"}</InfoRow>
              <InfoRow icon={IdentificationCard} label="Registered">
                <span className="num-display">{formatDate(selectedInvestor.created_at)}</span>
              </InfoRow>
              <div className="mt-4 rounded-xl border border-border bg-secondary/40 p-4">
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground mb-2">Set classification on approval</div>
                <Select value={pickClassification} onValueChange={setPickClassification}>
                  <SelectTrigger data-testid="pick-classification"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CLASSIFICATIONS.slice(0, 2).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => rejectInvestor(selectedInvestor)} disabled={busy} data-testid="reject-investor-btn" className="text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 border-red-200 dark:border-red-900">
              <XCircle size={16} className="mr-2" /> Reject
            </Button>
            <Button onClick={() => approveInvestor(selectedInvestor)} disabled={busy} data-testid="approve-investor-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <CheckCircle size={16} weight="fill" className="mr-2" /> Approve as {pickClassification}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Firm detail dialog */}
      <Dialog open={!!selectedFirm} onOpenChange={(o) => !o && setSelectedFirm(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{selectedFirm?.name}</DialogTitle>
          </DialogHeader>
          {selectedFirm && (
            <div className="mt-2">
              <InfoRow icon={Buildings} label="Firm name">{selectedFirm.name}</InfoRow>
              <InfoRow icon={EnvelopeSimple} label="Allowed domains">
                <div className="flex flex-wrap gap-1.5">
                  {(selectedFirm.domains || []).map((d) => (
                    <span key={d} className="inline-flex items-center px-2 py-0.5 rounded bg-secondary text-[11px] font-mono">@{d}</span>
                  ))}
                </div>
              </InfoRow>
              {selectedFirm.website && (
                <InfoRow icon={Globe} label="Website">
                  <a href={selectedFirm.website} target="_blank" rel="noreferrer" className="text-emerald-700 dark:text-emerald-400 hover:underline flex items-center gap-1">
                    {selectedFirm.website} <ArrowUpRight size={12} />
                  </a>
                </InfoRow>
              )}
              <InfoRow icon={User} label="Submitted by">{selectedFirm.requested_by_name || "—"}</InfoRow>
              <InfoRow icon={EnvelopeSimple} label="Submitter email">
                <a href={`mailto:${selectedFirm.requested_by_email}`} className="text-emerald-700 dark:text-emerald-400 hover:underline">
                  {selectedFirm.requested_by_email || "—"}
                </a>
                <span className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 font-bold uppercase tracking-wider">
                  <ShieldCheck size={10} weight="fill" /> Verified
                </span>
              </InfoRow>
              {selectedFirm.requested_by_role && (
                <InfoRow icon={IdentificationCard} label="Role">{selectedFirm.requested_by_role}</InfoRow>
              )}
              {selectedFirm.notes && (
                <InfoRow icon={ChatCircleText} label="Notes">
                  <div className="whitespace-pre-wrap">{selectedFirm.notes}</div>
                </InfoRow>
              )}
              <InfoRow icon={Clock} label="Submitted">
                <span className="num-display">{formatDate(selectedFirm.created_at)}</span>
              </InfoRow>
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => rejectFirm(selectedFirm)} disabled={busy} data-testid="reject-firm-btn" className="text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 border-red-200 dark:border-red-900">
              <XCircle size={16} className="mr-2" /> Reject
            </Button>
            <Button onClick={() => approveFirm(selectedFirm)} disabled={busy} data-testid="approve-firm-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <CheckCircle size={16} weight="fill" className="mr-2" /> Approve firm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmptyState({ heading, body, ctas = [] }) {
  return (
    <div className="rounded-2xl border border-dashed border-border p-16 text-center text-muted-foreground">
      <CheckCircle size={28} weight="fill" className="mx-auto mb-3 text-emerald-500/70" />
      <div className="font-display font-bold text-foreground text-lg">{heading}</div>
      <div className="mt-2 text-sm max-w-md mx-auto">{body}</div>
      {ctas.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2 justify-center">
          {ctas.map((cta) => {
            const Icon = cta.icon;
            const base = "inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm font-semibold transition-colors";
            const cls = cta.variant === "outline"
              ? `${base} border border-border bg-background hover:bg-secondary text-foreground`
              : `${base} bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white`;
            return (
              <Link key={cta.to} to={cta.to} data-testid={cta.testid} className={cls}>
                {Icon && <Icon size={15} />} {cta.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
